import { ICellRendererParams } from 'ag-grid-community';
import { FunctionComponent, useState, useRef, MouseEvent } from 'react';
import { TransactionModel } from '../../types/TransactionModel';
import { TransactionStatus } from '../../api/transactions/transaction.contracts';
import { useTranslation } from '@/lib';
import { Menu, MenuItem, CircularProgress } from '@mui/material';
import { KeyboardArrowRight } from '@mui/icons-material';
import { transactionService } from '../../api/transactions/transaction.service';
import { toast } from 'react-toastify';

export const TransactionStatusCellRenderer: FunctionComponent<ICellRendererParams<TransactionModel>> = (params) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  if (!params.data) {
    return null;
  }

  const status = params.data.status;
  const transactionId = params.data.id;
  const isFinalized = status === TransactionStatus.Finalized;

  const open = Boolean(anchorEl);

  // Get label based on status
  const getStatusLabel = (statusValue: TransactionStatus) => {
    switch (statusValue) {
      case TransactionStatus.InProgress:
        return t('in_progress');
      case TransactionStatus.Finalized:
        return t('finalized');
      default:
        return t('unknown');
    }
  };

  // Get color classes based on status
  const getColorClasses = (statusValue: TransactionStatus) => {
    switch (statusValue) {
      case TransactionStatus.InProgress:
        return {
          bg: 'bg-yellow-100 dark:bg-yellow-500/20',
          text: 'text-yellow-800 dark:text-yellow-300',
          hoverBg: 'hover:bg-yellow-200 dark:hover:bg-yellow-500/30',
        };
      case TransactionStatus.Finalized:
        return {
          bg: 'bg-green-100 dark:bg-green-500/20',
          text: 'text-green-800 dark:text-green-300',
          hoverBg: 'hover:bg-green-200 dark:hover:bg-green-500/30',
        };
      default:
        return {
          bg: 'bg-gray-100 dark:bg-gray-500/20',
          text: 'text-gray-800 dark:text-gray-300',
          hoverBg: 'hover:bg-gray-200 dark:hover:bg-gray-500/30',
        };
    }
  };

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    // Prevent opening menu for finalized transactions
    if (isFinalized) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (newStatus: TransactionStatus) => {
    if (newStatus === status) {
      handleClose();
      return;
    }

    setIsUpdating(true);
    handleClose();

    try {
      const response = await transactionService.updateStatus(transactionId, newStatus);

      if (response.isSuccess && response.payload) {
        toast.success(t('transaction_status_updated'));

        // Call the callback to trigger a full grid refresh
        if (params.context?.onStatusUpdated) {
          params.context.onStatusUpdated();
        }
      } else {
        toast.error(t('error_updating_transaction_status'));
      }
    } catch (error) {
      toast.error(t('error_updating_transaction_status'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleQuickFinalize = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (status === TransactionStatus.InProgress) {
      await handleStatusChange(TransactionStatus.Finalized);
    }
  };

  const colors = getColorClasses(status);

  return (
    <div className="flex h-full items-center">
      <div className="inline-flex mt-1">
        <button
          ref={buttonRef}
          onClick={handleClick}
          disabled={isUpdating || isFinalized}
          className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold transition-colors ${colors.bg} ${colors.text} ${
            !isFinalized ? colors.hoverBg : ''
          } disabled:opacity-50 ${status === TransactionStatus.InProgress ? 'rounded-l-md' : 'rounded-md'} ${
            !isFinalized ? 'cursor-pointer' : 'cursor-default'
          }`}
        >
          {isUpdating ? (
            <CircularProgress size={12} className={colors.text} />
          ) : (
            <>
              <span>{getStatusLabel(status)}</span>
            </>
          )}
        </button>

        {status === TransactionStatus.InProgress && !isUpdating && (
          <button
            onClick={handleQuickFinalize}
            className={`inline-flex items-center justify-center px-1 py-1 text-xs font-semibold transition-colors ${colors.bg} ${colors.text} ${colors.hoverBg} rounded-r-md border-l border-yellow-300/50 dark:border-yellow-600/50 cursor-pointer`}
            title={t('finalize')}
          >
            <KeyboardArrowRight fontSize="small" className="w-4 h-4" />
          </button>
        )}
      </div>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            className: '[&_.Mui-selected]:bg-black/[0.08] [&_.Mui-selected:hover]:bg-black/[0.22]',
          },
        }}
      >
        <MenuItem onClick={() => handleStatusChange(TransactionStatus.InProgress)} selected={status === TransactionStatus.InProgress}>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-md font-medium ${getColorClasses(TransactionStatus.InProgress).bg} ${
              getColorClasses(TransactionStatus.InProgress).text
            }`}
          >
            {getStatusLabel(TransactionStatus.InProgress)}
          </span>
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange(TransactionStatus.Finalized)} selected={status === TransactionStatus.Finalized}>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-md font-medium ${getColorClasses(TransactionStatus.Finalized).bg} ${
              getColorClasses(TransactionStatus.Finalized).text
            }`}
          >
            {getStatusLabel(TransactionStatus.Finalized)}
          </span>
        </MenuItem>
      </Menu>
    </div>
  );
};
